import { z } from "zod";

import {
  ANTHROPIC_ADAPTIVE_LEVELS,
  ANTHROPIC_THINKING_DISPLAY_VALUES
} from "./thinking-dialect.registry";

export { ANTHROPIC_THINKING_DISPLAY_VALUES };

// =============================================================================
// Zod schemas for Anthropic thinking shapes (kept here for cross-package use)
// =============================================================================

export const AnthropicLegacyThinkingConfigSchema = z.object({
  type: z.literal("enabled"),
  budget_tokens: z.number().int().positive(),
  display: z.enum(ANTHROPIC_THINKING_DISPLAY_VALUES).optional()
});

export const AnthropicAdaptiveThinkingConfigSchema = z.object({
  type: z.literal("adaptive"),
  display: z.enum(ANTHROPIC_THINKING_DISPLAY_VALUES).optional()
});

export const AnthropicDisabledThinkingSchema = z.object({
  type: z.literal("disabled")
});

export const AnthropicThinkingConfigSchema = z.discriminatedUnion("type", [
  AnthropicLegacyThinkingConfigSchema,
  AnthropicAdaptiveThinkingConfigSchema,
  AnthropicDisabledThinkingSchema
]);

export type AnthropicThinkingConfig = z.infer<typeof AnthropicThinkingConfigSchema>;
export type AnthropicLegacyThinkingConfig = z.infer<typeof AnthropicLegacyThinkingConfigSchema>;
export type AnthropicAdaptiveThinkingConfig = z.infer<typeof AnthropicAdaptiveThinkingConfigSchema>;

export const AnthropicOutputConfigSchema = z.object({
  effort: z.enum(ANTHROPIC_ADAPTIVE_LEVELS)
});
export type AnthropicOutputConfig = z.infer<typeof AnthropicOutputConfigSchema>;

/**
 * Conservative Anthropic output ceiling used ONLY when a model is absent from the catalog.
 * Held below every Anthropic ceiling the catalog carries, so an unknown model never
 * exceeds the provider's accepted `max_tokens`. Not a request budget.
 */
export const ANTHROPIC_FALLBACK_CEILING = 32_000;

/**
 * What a CUSTOM endpoint's request carries as `max_tokens` when the endpoint has declared
 * no ceiling of its own.
 *
 * 🔴 **NOT {@link ANTHROPIC_FALLBACK_CEILING}, AND THE DIFFERENCE IS WHICH FAILURE YOU GET.**
 * 32,000 sits below every ceiling in the catalog, chosen so an unknown PLATFORM model never
 * 400s. Applied to a customer's endpoint it silently truncates every model that accepts more
 * — the customer's own 128K-capable endpoint stops generating at 32K, with `stop_reason:
 * "max_tokens"` and nothing anywhere saying we asked for a quarter of what it offered.
 *
 * 128,000 is the highest `max_tokens` Anthropic grants on any current model. Measured
 * 2026-09-18 against api.anthropic.com, both directions: `claude-sonnet-5` and
 * `claude-opus-4-6` both accept exactly 128000 (HTTP 200), and 128001 on `claude-sonnet-5`
 * is refused. So an endpoint that can serve the full ceiling now does, and one that cannot
 * says so BY NAME instead of truncating — the refusal carries the exact number to declare:
 *
 *   HTTP 400 invalid_request_error
 *   "max_tokens: 128000 > 64000, which is the maximum allowed number of output tokens
 *    for claude-sonnet-4-5-20250929"
 *
 * Reproduced at four overshoot magnitudes (64001, 128000, 128001, 200000, 1000000) — the
 * message names the model's true ceiling every time, which is what makes a loud refusal
 * strictly more useful here than a quiet truncation. That is the whole trade: this value
 * converts an invisible loss into a self-describing 400 the operator can act on once.
 *
 * A higher sentinel would refuse EVERY endpoint including the largest, so it is capped at
 * what the vendor actually grants rather than at an arbitrary large number.
 */
export const ANTHROPIC_UNDECLARED_ENDPOINT_CEILING = 128_000;
